// src/utils/printReport.js
/* ------------------------------------------------------------------
   Energiberegner - PDF report generator
   Used by <PrintModal/> via downloadPdfReport(options)

   Notes:
   - Supports basePath/assetPrefix so logo and fonts load in dev/prod
   - Registers Unicode fonts to ensure lambda renders correctly
------------------------------------------------------------------- */

import React from "react";
import {
    pdf,
    Document,
    Page,
    View,
    Text,
    Image,
    StyleSheet,
    Font,
} from "@react-pdf/renderer";

import formatNumber from "./formatNumber";
import { layerR, calcUValue } from "./uvalueCalculations";
import { rowHeatLoss } from "./varmetabCalculations";
import { PLAN_TYPES, LINE_TYPES, KIND, getRefU, stripAutoRows } from "./varmetabModel";
import { insulationR } from "../components/calculators/uvalue/constructioncard/logic";

import matsRaw from "../data/materials.json";
import indoorsRaw from "../data/indvendigeOvergangsisolans.json";
import outdoorsRaw from "../data/udvendigeOvergangsisolans.json";

const mats = matsRaw.materials ?? matsRaw;
const indoors = indoorsRaw.indvendigeOvergangsisolans ?? indoorsRaw;
const outdoors = outdoorsRaw.udvendigeOvergangsisolans ?? outdoorsRaw;

const MONTHS_DA = [
    "januar",
    "februar",
    "marts",
    "april",
    "maj",
    "juni",
    "juli",
    "august",
    "september",
    "oktober",
    "november",
    "december",
];

const LAMBDA = "\u03BB";
const DELTA = "\u0394";
const STAR = "\u2605";
const fmt = (v, d = 2) => formatNumber(v, d);

const TILLAEG_TINY_THRESHOLD = 0.005;
const ALPHA = 0.8;

function joinUrlParts(...parts) {
    const cleaned = parts
        .filter(Boolean)
        .map((p, i) => {
            const s = String(p);
            if (i === 0) return s.replace(/\/+$/, "");
            return s.replace(/^\/+/, "").replace(/\/+$/, "");
        });

    return cleaned.join("/").replace(/([^:]\/)\/+/g, "$1");
}

const withBase = (p = "", opts = {}) => {
    const envBasePath = process.env.NEXT_PUBLIC_BASE_PATH || "";
    const envAssetPrefix = process.env.NEXT_PUBLIC_ASSET_PREFIX || "";

    const basePath = (opts.basePath ?? envBasePath ?? "").trim();
    const assetPrefix = (opts.assetPrefix ?? envAssetPrefix ?? "").trim();

    const path = String(p).replace(/^\/+/, "");
    const urlPath = joinUrlParts(basePath, path);

    if (assetPrefix) return joinUrlParts(assetPrefix, urlPath);
    return `/${urlPath.replace(/^\/+/, "")}`;
};

async function asDataURI(url) {
    try {
        const abs =
            typeof window !== "undefined"
                ? new URL(url, window.location.origin).toString()
                : url;

        const res = await fetch(abs);
        if (!res.ok) return null;

        const blob = await res.blob();

        return await new Promise((ok) => {
            const fr = new FileReader();
            fr.onloadend = () => ok(fr.result);
            fr.readAsDataURL(blob);
        });
    } catch {
        return null;
    }
}

let _fontReady = false;

async function ensurePdfFont(opts = {}) {
    if (_fontReady) return;
    if (typeof window === "undefined") return;

    const reg = async (family, src, weight = "normal") => {
        try {
            Font.register({ family, src, fontWeight: weight });
            return true;
        } catch {
            return false;
        }
    };

    const dejavu = await asDataURI(withBase("fonts/DejaVuSans.ttf", opts));
    const dejavuBold = await asDataURI(withBase("fonts/DejaVuSans-Bold.ttf", opts));

    if (dejavu) {
        await reg("DejaVuSans", dejavu, "normal");
        if (dejavuBold) await reg("DejaVuSans", dejavuBold, "bold");
        _fontReady = true;
        return;
    }

    const noto = await asDataURI(withBase("fonts/NotoSans-Regular.ttf", opts));
    const notoBold = await asDataURI(withBase("fonts/NotoSans-Bold.ttf", opts));

    if (noto) {
        await reg("DejaVuSans", noto, "normal");
        if (notoBold) await reg("DejaVuSans", notoBold, "bold");
        _fontReady = true;
    }
}

const matchByValue = (arr, val, tol = 1e-6) =>
    arr.find((o) => Math.abs(o.rValue - val) < tol);

const styles = StyleSheet.create({
    page: { padding: 32, fontSize: 10, fontFamily: "DejaVuSans" },

    h1: { fontSize: 20, fontWeight: "bold" },
    h2: { fontSize: 13, fontWeight: "bold", marginTop: 14, marginBottom: 6 },
    p: { fontSize: 11, lineHeight: 1.3, marginBottom: 6 },

    table: { width: "100%", border: "1 solid #ccc", borderCollapse: "collapse" },
    row: { flexDirection: "row" },
    cell: { padding: 4, borderRight: "1 solid #ccc", justifyContent: "center" },

    head: { backgroundColor: "#e8e8e8", fontWeight: "bold" },
    tot1: { backgroundColor: "#f3f3f3", fontWeight: "bold" },
    tot2: { backgroundColor: "#ebebeb", fontWeight: "bold" },

    headerWrap: { marginBottom: 10 },
    headerTop: {
        flexDirection: "row",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 8,
    },
    metaCol: { flex: 1, paddingRight: 8 },
    logo: { width: 120, height: 40, objectFit: "contain" },

    coverGrid: { marginTop: 6 },
    kvRow: { flexDirection: "row", marginBottom: 4 },
    kvKey: { width: 120, color: "#555" },
    kvVal: { flex: 1, fontSize: 12 },

    hr: { height: 1, backgroundColor: "#ddd", marginTop: 10, marginBottom: 10 },

    allowanceBox: {
        marginTop: 6,
        padding: 6,
        border: "1 solid #ddd",
        backgroundColor: "#fafafa",
    },
    allowanceTitle: { fontSize: 10, fontWeight: "bold", marginBottom: 2 },
    allowanceLine: { fontSize: 10, lineHeight: 1.25 },

    footer: {
        position: "absolute",
        bottom: 18,
        left: 32,
        right: 32,
        fontSize: 9,
        color: "#666",
        flexDirection: "row",
        justifyContent: "space-between",
    },
});

function toNum(value, fallback = NaN) {
    if (value === "" || value === null || value === undefined) return fallback;
    if (typeof value === "number") return Number.isFinite(value) ? value : fallback;

    const n = Number(String(value).trim().replace(",", "."));
    return Number.isFinite(n) ? n : fallback;
}

function pickNum(...values) {
    for (const value of values) {
        const n = toNum(value);
        if (Number.isFinite(n)) return n;
    }
    return NaN;
}

function firstNonEmpty(obj, keys) {
    if (!obj) return undefined;

    for (const key of keys) {
        const value = obj[key];
        if (value !== undefined && value !== null && value !== "") return value;
    }

    return undefined;
}

function asList(value) {
    return Array.isArray(value) ? value : [];
}

function formatDelta(delta, decimals = 3) {
    if (!Number.isFinite(Number(delta))) return "-";

    const d = Number(delta);
    const abs = Math.abs(d);

    if (abs > 0 && abs < TILLAEG_TINY_THRESHOLD) {
        return `< ${fmt(TILLAEG_TINY_THRESHOLD, 3)} W/m²K`;
    }

    return `${fmt(d, decimals)} W/m²K`;
}

function calcDeltaUb(lambda, n, d_mm, d1_m, R1, RT) {
    if (!lambda || !d1_m || !n || !d_mm || !R1 || !RT) return 0;
    const Ab = Math.PI * (d_mm ** 2) / 4e6;
    return (ALPHA * lambda * Ab * n / d1_m) * ((R1 / RT) ** 2);
}

function formatLambdaMaybe(v) {
    if (v == null) return "-";
    const n = Number(v);
    if (Number.isFinite(n)) return fmt(n, 0);
    return String(v);
}

function formatLenMaybe(v) {
    if (v == null || v === "") return "-";
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v);
    return `${fmt(n, 2)} m`;
}

function buildAllowanceLines(card) {
    const a = card?.allow || {};
    const lines = [];

    if (a?.addA2) {
        lines.push(`A.2: ${DELTA}U${STAR} = ${formatDelta(card?.deltaA2, 3)}`);
    }

    if (a?.addA3) {
        const n = a.nFast ?? "-";
        const d = a.dia ? `Ø${a.dia} mm` : "Ø-";
        const lambda = formatLambdaMaybe(a.lambdaFast ?? a.lambdaFastMode);
        const L = formatLenMaybe(a.len);

        lines.push(
            `A.3: ${DELTA}U = ${formatDelta(card?.deltaA3, 3)} (${n}× ${d}, ${LAMBDA} ${lambda}, d1 ${L})`
        );
    }

    return lines;
}

function layerLabel(l) {
    if (l.customName?.trim()) return l.customName.trim();
    if (l.itemName?.trim()) return l.itemName.trim();

    if (l.catIndex != null && l.itemIndex != null) {
        const cat = mats[l.catIndex];
        const item = cat?.items?.[l.itemIndex];
        if (item?.name) return item.name;
    }

    return l.name || l.label || l.title || "Lag";
}

function transitionDesc(kind, card) {
    if (kind === "rsi") {
        if (card.RsiDesc || card.RsiLabel) return card.RsiDesc || card.RsiLabel;
        if (card.RsiIdx != null) return indoors[card.RsiIdx]?.description;
        return matchByValue(indoors, card.RsiVal)?.description || "Indvendig overgang";
    }

    if (card.RseDesc || card.RseLabel) return card.RseDesc || card.RseLabel;
    if (card.RseIdx != null) return outdoors[card.RseIdx]?.description;

    return matchByValue(outdoors, card.RseVal)?.description || "Udvendig overgang";
}

function enrichCardForReport(card) {
    const Rsi = pickNum(card?.RsiVal) || 0;
    const Rse = pickNum(card?.RseVal) || 0;
    const layers = Array.isArray(card.layers) ? card.layers : [];

    const rLayers = layers.reduce((s, l) => s + (layerR(l) || 0), 0);
    const totalR = (rLayers + Rsi + Rse) || 0;

    const totalThickness_m = layers.reduce(
        (s, l) => s + (pickNum(l?.thickness_m) || 0),
        0
    );

    const allow = card?.allow || {};
    const RT_allow = pickNum(card?.RT, card?.Rt, card?.rt, totalR) || totalR;

    const Ri_fallback = layers.reduce((s, l) => {
        const r = insulationR(l, mats);
        return s + (Number.isFinite(r) ? r : 0);
    }, 0);

    const Ri = pickNum(card?.Ri, card?.RI, card?.ri, allow?.Ri, Ri_fallback) || 0;

    const ids = Array.isArray(allow?.r1LayerIds) ? allow.r1LayerIds : [];

    const R1_fromIds =
        allow?.r1Mode === "manual" && ids.length
            ? layers.reduce((s, l) => (ids.includes(l.id) ? s + (insulationR(l, mats) || 0) : s), 0)
            : Ri;

    const R1 = pickNum(allow?.R1, card?.R1, R1_fromIds, Ri) || 0;

    const Ustar = pickNum(allow?.Ustar, allow?.ustar) || 0;
    const ratio = RT_allow ? (Ri / RT_allow) : 0;

    const deltaA2 =
        allow?.addA2 && Ustar > 0 && RT_allow > 0
            ? Ustar * (ratio ** 2)
            : 0;

    const lambdaEff =
        allow?.lambdaFastMode === "custom"
            ? pickNum(allow?.lambdaFast)
            : pickNum(allow?.lambdaFastMode, allow?.lambdaFast);

    const nFast = pickNum(allow?.nFast);
    const dia = pickNum(allow?.dia);
    const d1 = pickNum(allow?.len);

    const deltaA3 =
        allow?.addA3
            ? calcDeltaUb(
                Number.isFinite(lambdaEff) ? lambdaEff : 0,
                Number.isFinite(nFast) ? nFast : 0,
                Number.isFinite(dia) ? dia : 0,
                Number.isFinite(d1) ? d1 : 0,
                R1,
                RT_allow
            )
            : 0;

    const totalU = calcUValue(layers, { Rsi, Rse, ...allow, Ri, R1 });

    return {
        ...card,
        RsiVal: Rsi,
        RseVal: Rse,
        totalR,
        totalU,
        totalThickness_m,
        deltaA2,
        deltaA3,
    };
}

function HeaderBlock({ projectName, author, reportNote, dateStr, logoData }) {
    return (
        <View style={styles.headerWrap}>
            <View style={styles.headerTop}>
                <View style={styles.metaCol}>
                    <Text style={styles.h1}>Energiberegner - Rapport</Text>

                    <View style={styles.coverGrid}>
                        <View style={styles.kvRow}>
                            <Text style={styles.kvKey}>Projekt</Text>
                            <Text style={styles.kvVal}>{projectName || "-"}</Text>
                        </View>

                        <View style={styles.kvRow}>
                            <Text style={styles.kvKey}>Udarbejdet af</Text>
                            <Text style={styles.kvVal}>{author || "-"}</Text>
                        </View>

                        <View style={styles.kvRow}>
                            <Text style={styles.kvKey}>Dato</Text>
                            <Text style={styles.kvVal}>{dateStr}</Text>
                        </View>
                    </View>
                </View>

                {!!logoData && <Image src={logoData} style={styles.logo} />}
            </View>

            {!!reportNote && (
                <>
                    <View style={styles.hr} />
                    <Text style={styles.p}>{reportNote}</Text>
                </>
            )}

            <View style={styles.hr} />
        </View>
    );
}

function Footer({ projectName }) {
    return (
        <View style={styles.footer} fixed>
            <Text>{projectName ? String(projectName) : "Energiberegner"}</Text>
            <Text render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
        </View>
    );
}

function renderConstruction(card, idx) {
    const rows = [];

    rows.push(["Rsi", transitionDesc("rsi", card), "-", fmt(card.RsiVal, 3)]);

    (card.layers || []).forEach((l, i) => {
        let desc = layerLabel(l);

        if (l.type === "homogeneous") {
            if (l.kind === "lambda") desc += ` (${LAMBDA} ${fmt(l.lambda, 3)})`;
            else desc += ` (R ${fmt(l.rValue, 3)})`;
        } else if (l.type === "composite") {
            if (l.lambdaIns != null) {
                desc += ` (${LAMBDA} ${fmt(l.lambdaIns, 3)})`;
            } else {
                const R = layerR(l);
                if (l.thickness_m && R) desc += ` (${LAMBDA} ${fmt(l.thickness_m / R, 3)})`;
            }
        } else if (l.type === "customMaterial") {
            if (l.mode === "lambda") desc += ` (${LAMBDA} ${fmt(l.customLambda, 3)})`;
            else desc += ` (R ${fmt(l.customR, 3)})`;
        }

        rows.push([
            i + 1,
            desc,
            l.thickness_m ? fmt(l.thickness_m, 3) : "-",
            fmt(layerR(l), 3),
        ]);
    });

    rows.push(["Rse", transitionDesc("rse", card), "-", fmt(card.RseVal, 3)]);
    rows.push(["", "Total isolans", "-", fmt(card.totalR, 3)]);
    rows.push([
        "U-værdi",
        `${fmt(card.totalU, 2)} W/m²K`,
        fmt(card.totalThickness_m, 3),
        "",
    ]);

    const note = (card.note || "").trim();
    const allowLines = buildAllowanceLines(card);

    return (
        <View
            key={card.id || idx}
            wrap={false}
            minPresenceAhead={140}
            style={{ marginBottom: 12 }}
        >
            <Text style={{ fontSize: 11, fontWeight: "bold", marginBottom: 2 }}>
                {idx}. {card.title || "Konstruktion"}
            </Text>

            {note && <Text style={{ fontSize: 10, marginBottom: 4 }}>{note}</Text>}

            <View style={styles.table}>
                <View style={[styles.row, styles.head]}>
                    {["#", "Materiale / lag", "Tykkelse [m]", "Isolans [m²K/W]"].map((h, i) => (
                        <Text key={h} style={[styles.cell, { flex: i === 1 ? 3 : 1 }]}>
                            {h}
                        </Text>
                    ))}
                </View>

                {rows.map((r, i) => {
                    const isTotalIsolans = String(r?.[1] ?? "") === "Total isolans";
                    const isU = String(r?.[0] ?? "") === "U-værdi";
                    const extra = isTotalIsolans ? styles.tot1 : isU ? styles.tot2 : null;
                    const gap = isU ? { marginTop: 4 } : null;

                    return (
                        <View key={i} style={[styles.row, extra, gap]}>
                            {r.map((c, j) => (
                                <Text key={j} style={[styles.cell, { flex: j === 1 ? 3 : 1 }]}>
                                    {c}
                                </Text>
                            ))}
                        </View>
                    );
                })}
            </View>

            {allowLines.length > 0 && (
                <View style={styles.allowanceBox}>
                    <Text style={styles.allowanceTitle}>Tillæg</Text>
                    {allowLines.map((t, i) => (
                        <Text key={i} style={styles.allowanceLine}>
                            {t}
                        </Text>
                    ))}
                </View>
            )}
        </View>
    );
}

function UvalueSection({ deck = [] }) {
    if (!deck.length) return null;

    const enriched = deck.map(enrichCardForReport);

    return (
        <View>
            <Text style={styles.h2}>U-værdi-beregner</Text>
            {enriched.map((c, i) => renderConstruction(c, i + 1))}
        </View>
    );
}
function normaliseVarmetabData(data) {
    if (!data) return null;

    const raw =
        data?.payload ??
        data?.state ??
        data?.result ??
        data?.data ??
        data?.varmetab?.payload ??
        data?.varmetab?.state ??
        data?.varmetab?.data ??
        data?.varmetab ??
        data;

    const payload =
        raw?.frames?.ramme ??
        raw?.ramme ??
        raw?.payload ??
        raw?.state ??
        raw?.data ??
        raw;

    if (!payload || typeof payload !== "object") return null;

    return {
        ...payload,
        info: data?.info ?? raw?.info ?? payload?.info ?? {},
    };
}

function normaliseVarmetabRow(row = {}) {
    return {
        ...row,
        UorPsi: toNum(firstNonEmpty(row, [
            "UorPsi",
            "U_or_Psi",
            "U",
            "Uvalue",
            "UValue",
            "U_v",
            "U_Value",
            "Psi",
            "psi",
            "Ufoer",
            "Uefter",
            "U_før",
            "U_efter",
        ]), 0),
        AorL: toNum(firstNonEmpty(row, [
            "AorL",
            "A_or_L",
            "Areal",
            "area",
            "area_m2",
            "Areal_m2",
            "A",
            "L",
            "Length",
            "length",
            "længde",
        ]), 0),
        Ti: toNum(firstNonEmpty(row, ["Ti", "T_i", "tempIn", "temperatureInside"]), 20),
        Te: toNum(firstNonEmpty(row, ["Te", "T_e", "tempOut", "temperatureOutside"]), -12),
    };
}

function hasUsefulVarmetabValue(row = {}) {
    const a = toNum(row.AorL, 0);
    const u = toNum(row.UorPsi, 0);

    return Math.abs(a) > 0 || Math.abs(u) > 0;
}

function isAutoVarmetabRow(row = {}) {
    const id = String(row.id || "");
    return id.startsWith("__add") || id.startsWith("__extra") || id === "__linje_hdr__" || id === "__divider__";
}

function cleanVarmetabRows(rows = []) {
    return asList(rows)
        .map(normaliseVarmetabRow)
        .filter((r) => {
            if (!r || isAutoVarmetabRow(r)) return false;
            return hasUsefulVarmetabValue(r);
        });
}

function hasVarmetabRows(data) {
    const payload = normaliseVarmetabData(data);
    if (!payload) return false;

    return [
        "existPlan",
        "existLine",
        "coverPlan",
        "coverLine",
        "designPlan",
        "designLine",
    ].some((key) => cleanVarmetabRows(payload[key]).length > 0);
}

function rowLabel(row = {}) {
    const isLine = row.partKind === KIND.LINE || row.partKind === "line";
    const list = isLine ? LINE_TYPES : PLAN_TYPES;

    const typeName = row.typeKey != null ? list[row.typeKey]?.name ?? "" : "";
    const userName = String(row.bygningsdel ?? "").trim();

    if (
        userName &&
        typeName &&
        userName.toLowerCase() !== typeName.toLowerCase()
    ) {
        return `${userName} (${typeName})`;
    }

    return userName || typeName || "-";
}

function isGlassName(name = "") {
    return /^(vinduer|ovenlysvinduer?|ovenlyskupler?|yderdøre?\s*med\s*glas)$/i.test(name);
}

function isWallName(name = "") {
    return /ydervæg$/i.test(name);
}

function sumHeatLoss(rows) {
    return cleanVarmetabRows(rows).reduce(
        (sum, row) => sum + rowHeatLoss(row),
        0
    );
}

function buildVarmetabReferenceRows({
    designPlan = [],
    designLine = [],
    coverPlan = [],
    bType = "helårs",
    floorArea = "",
}) {
    const floorAreaNum = toNum(floorArea, NaN);
    const limitPerc = bType === "sommer" ? 0.30 : 0.22;

    const baseDesignPlan = cleanVarmetabRows(stripAutoRows(designPlan))
        .filter((r) => r.partKind !== KIND.LINE && r.partKind !== "line");

    const baseDesignLine = cleanVarmetabRows(designLine)
        .filter((r) => r.partKind === KIND.LINE || r.partKind === "line");

    const glassLimit =
        Number.isFinite(floorAreaNum) && floorAreaNum > 0
            ? floorAreaNum * limitPerc
            : null;

    const glassArea = baseDesignPlan.reduce((sum, row) => {
        const name = PLAN_TYPES[row.typeKey]?.name ?? "";
        return isGlassName(name) ? sum + (Number(row.AorL) || 0) : sum;
    }, 0);

    const extraGlass =
        glassLimit != null
            ? Math.max(0, glassArea - glassLimit)
            : 0;

    const refPlanBase = baseDesignPlan.map((row) => {
        const src = PLAN_TYPES[row.typeKey] ?? {};
        const ref = getRefU(src, bType) ?? src.Udefault ?? row.UorPsi ?? 0;

        return normaliseVarmetabRow({
            ...row,
            id: `__ref_plan_${row.id}`,
            partKind: KIND.PLAN,
            bygningsdel: row.bygningsdel || src?.name || "-",
            UorPsi: ref,
            AorL: Number(row.AorL) || 0,
            Ti: row.Ti ?? 20,
            Te: row.Te ?? -12,
            locked: true,
        });
    });

    const firstDesignGlassTypeKey =
        baseDesignPlan.find((row) =>
            row.typeKey != null && isGlassName(PLAN_TYPES[row.typeKey]?.name)
        )?.typeKey ??
        PLAN_TYPES.findIndex((type) => isGlassName(type?.name));

    const firstDesignWallTypeKey =
        baseDesignPlan.find((row) =>
            row.typeKey != null && isWallName(PLAN_TYPES[row.typeKey]?.name)
        )?.typeKey ??
        PLAN_TYPES.findIndex((type) => isWallName(type?.name));

    const refPlanGlassAdjustment = [];

    if (extraGlass > 0) {
        const winType = PLAN_TYPES[firstDesignGlassTypeKey] ?? {};
        const wallType = PLAN_TYPES[firstDesignWallTypeKey] ?? {};

        const winU = getRefU(winType, bType) ?? winType.Udefault ?? 1.2;
        const wallU = getRefU(wallType, bType) ?? wallType.Udefault ?? 0.15;

        refPlanGlassAdjustment.push(
            normaliseVarmetabRow({
                id: "__extra_glass_neg_pdf",
                partKind: KIND.PLAN,
                bygningsdel: "Justering: overskydende glas",
                typeKey: firstDesignGlassTypeKey >= 0 ? firstDesignGlassTypeKey : null,
                UorPsi: winU,
                AorL: -extraGlass,
                Ti: 20,
                Te: -12,
                locked: true,
            }),
            normaliseVarmetabRow({
                id: "__extra_glass_pos_pdf",
                partKind: KIND.PLAN,
                bygningsdel: "Justering: regnes som ydervæg",
                typeKey: firstDesignWallTypeKey >= 0 ? firstDesignWallTypeKey : null,
                UorPsi: wallU,
                AorL: extraGlass,
                Ti: 20,
                Te: -12,
                locked: true,
            })
        );
    }

    const refPlanCover = cleanVarmetabRows(coverPlan)
        .filter((row) =>
            row.partKind === KIND.PLAN ||
            row.partKind === "plan" ||
            row.partKind == null
        )
        .filter((row) => Number(row.AorL) > 0 && Number(row.UorPsi) > 0)
        .map((row, index) =>
            normaliseVarmetabRow({
                ...row,
                id: `__ref_deduct_pdf_${index}`,
                partKind: KIND.PLAN,
                bygningsdel: `Fradrag 50% - ${row.bygningsdel || "dækket konstruktion"}`,
                UorPsi: Number(row.UorPsi),
                AorL: 0.5 * Number(row.AorL),
                Ti: row.Ti ?? 20,
                Te: row.Te ?? -12,
                locked: true,
            })
        );

    const refPlan = [
        ...refPlanBase,
        ...refPlanGlassAdjustment,
        ...refPlanCover,
    ];

    const refLine = baseDesignLine.map((row) => {
        const src = LINE_TYPES[row.typeKey] ?? {};
        const ref = getRefU(src, bType) ?? src.Udefault ?? row.UorPsi ?? 0;

        return normaliseVarmetabRow({
            ...row,
            id: `__ref_line_${row.id}`,
            partKind: KIND.LINE,
            bygningsdel: row.bygningsdel || src?.name || "Linjetab",
            UorPsi: ref,
            AorL: Number(row.AorL) || 0,
            Ti: row.Ti ?? src.tInd ?? 20,
            Te: row.Te ?? src.tUde ?? -12,
            locked: true,
        });
    });

    return {
        refPlan,
        refLine,
        glassLimit,
        glassArea,
        extraGlass,
    };
}

function renovationRequirementFor(row) {
    const isLine = row.partKind === KIND.LINE || row.partKind === "line";

    if (isLine) {
        const src = LINE_TYPES[row.typeKey];
        if (!src) return null;

        const limit =
            (typeof src.renoPsiMax === "number" ? src.renoPsiMax : null) ??
            (typeof src.psiMax === "number" ? src.psiMax : null) ??
            (typeof src.Udefault === "number" ? src.Udefault : null);

        if (limit == null) return null;

        const usedFallback = src.renoPsiMax == null && src.psiMax == null;

        return {
            limit,
            unit: "W/m·K",
            usedFallback,
        };
    }

    const src = PLAN_TYPES[row.typeKey];
    if (!src) return null;

    const limit =
        (typeof src.renoMax === "number" ? src.renoMax : null) ??
        (typeof src.uMax === "number" ? src.uMax : null) ??
        (typeof src.Udefault === "number" ? src.Udefault : null);

    if (limit == null) return null;

    const usedFallback = src.renoMax == null && src.uMax == null;

    return {
        limit,
        unit: "W/m²K",
        usedFallback,
    };
}

function renovationVerdict(actual, limit) {
    if (actual == null || limit == null) {
        return {
            ok: null,
            diff: null,
        };
    }

    const ok = actual <= limit;

    return {
        ok,
        diff: actual - limit,
    };
}

function getRenovationComplianceRows({ designPlan = [], designLine = [] }) {
    const planRows = cleanVarmetabRows(designPlan).filter((row) =>
        row.typeKey != null &&
        Number(row.AorL) > 0 &&
        !isAutoVarmetabRow(row)
    );

    const lineRows = cleanVarmetabRows(designLine).filter((row) =>
        row.typeKey != null &&
        Number(row.AorL) > 0 &&
        !isAutoVarmetabRow(row)
    );

    return [...planRows, ...lineRows].map((row) => {
        const req = renovationRequirementFor(row);
        const actual = Number.isFinite(row.UorPsi) ? row.UorPsi : null;
        const verdict = renovationVerdict(actual, req?.limit ?? null);

        return {
            row,
            req,
            actual,
            verdict,
        };
    });
}

function RenovationComplianceTable({ designPlan = [], designLine = [] }) {
    const rows = getRenovationComplianceRows({ designPlan, designLine });

    if (!rows.length) {
        return (
            <View style={{ marginTop: 10 }}>
                <Text style={{ fontSize: 11, fontWeight: "bold", marginBottom: 3 }}>
                    Komponentkrav (BR18 - renovering)
                </Text>
                <Text>
                    Udfyld type og et positivt areal/længde for at se BR18-krav.
                </Text>
            </View>
        );
    }

    const anyFallback = rows.some((item) => item.req?.usedFallback);

    return (
        <View style={{ marginTop: 10 }}>
            <Text style={{ fontSize: 11, fontWeight: "bold", marginBottom: 3 }}>
                Komponentkrav (BR18 - renovering)
            </Text>

            <View style={styles.table}>
                <View style={[styles.row, styles.head]}>
                    <Text style={[styles.cell, { flex: 2.4 }]}>Bygningsdel</Text>
                    <Text style={[styles.cell, { flex: 1.1 }]}>Krav</Text>
                    <Text style={[styles.cell, { flex: 1.1 }]}>Design</Text>
                    <Text style={[styles.cell, { flex: 1.1 }]}>Afvigelse</Text>
                    <Text style={[styles.cell, { flex: 0.9 }]}>Status</Text>
                </View>

                {rows.map((item, index) => {
                    const { row, req, actual, verdict } = item;

                    const reqTxt = req
                        ? `${fmt(req.limit, 2)} ${req.unit}`
                        : "-";

                    const actualTxt = actual != null
                        ? `${fmt(actual, 2)} ${req?.unit ?? ""}`
                        : "-";

                    const diffTxt = verdict.diff != null
                        ? `${verdict.diff >= 0 ? "+" : ""}${fmt(verdict.diff, 2)} ${req?.unit ?? ""}`
                        : "-";

                    const statusTxt =
                        verdict.ok == null
                            ? "-"
                            : verdict.ok
                                ? "OK"
                                : "Ikke OK";

                    const rowStyle =
                        verdict.ok == null
                            ? null
                            : verdict.ok
                                ? { backgroundColor: "#e8f5e9" }
                                : { backgroundColor: "#ffebee" };

                    return (
                        <View key={row.id || index} style={[styles.row, rowStyle]}>
                            <Text style={[styles.cell, { flex: 2.4 }]}>
                                {rowLabel(row)}
                            </Text>
                            <Text style={[styles.cell, { flex: 1.1 }]}>
                                {reqTxt}
                            </Text>
                            <Text style={[styles.cell, { flex: 1.1 }]}>
                                {actualTxt}
                            </Text>
                            <Text style={[styles.cell, { flex: 1.1 }]}>
                                {diffTxt}
                            </Text>
                            <Text style={[styles.cell, { flex: 0.9 }]}>
                                {statusTxt}
                            </Text>
                        </View>
                    );
                })}
            </View>

            {anyFallback && (
                <Text style={{ marginTop: 4, fontSize: 9 }}>
                    Bemærk: Enkelte krav bruger standard-/fallbackværdi, fordi der ikke er angivet et særskilt renoveringskrav i datagrundlaget.
                </Text>
            )}
        </View>
    );
}

function VarmetabRowsTable({ title, rows }) {
    const list = cleanVarmetabRows(rows);

    if (!list.length) return null;

    return (
        <View style={{ marginTop: 8 }}>
            <Text style={{ fontSize: 11, fontWeight: "bold", marginBottom: 3 }}>
                {title}
            </Text>

            <View style={styles.table}>
                <View style={[styles.row, styles.head]}>
                    <Text style={[styles.cell, { flex: 0.8 }]}>Type</Text>
                    <Text style={[styles.cell, { flex: 2.3 }]}>Bygningsdel</Text>
                    <Text style={[styles.cell, { flex: 1 }]}>U/Psi</Text>
                    <Text style={[styles.cell, { flex: 1 }]}>A/L</Text>
                    <Text style={[styles.cell, { flex: 0.8 }]}>Ti</Text>
                    <Text style={[styles.cell, { flex: 0.8 }]}>Te</Text>
                    <Text style={[styles.cell, { flex: 1 }]}>W</Text>
                </View>

                {list.map((row, index) => {
                    const isLine = row.partKind === KIND.LINE || row.partKind === "line";

                    return (
                        <View key={row.id || index} style={styles.row}>
                            <Text style={[styles.cell, { flex: 0.8 }]}>
                                {isLine ? "Linje" : "Flade"}
                            </Text>
                            <Text style={[styles.cell, { flex: 2.3 }]}>
                                {rowLabel(row)}
                            </Text>
                            <Text style={[styles.cell, { flex: 1 }]}>
                                {fmt(row.UorPsi, 2)}
                            </Text>
                            <Text style={[styles.cell, { flex: 1 }]}>
                                {fmt(row.AorL, 2)}
                            </Text>
                            <Text style={[styles.cell, { flex: 0.8 }]}>
                                {fmt(row.Ti, 0)}
                            </Text>
                            <Text style={[styles.cell, { flex: 0.8 }]}>
                                {fmt(row.Te, 0)}
                            </Text>
                            <Text style={[styles.cell, { flex: 1 }]}>
                                {fmt(rowHeatLoss(row), 1)}
                            </Text>
                        </View>
                    );
                })}
            </View>
        </View>
    );
}

function RenovationDesignRowsTable({ title, rows }) {
    const list = cleanVarmetabRows(rows);

    if (!list.length) return null;

    return (
        <View style={{ marginTop: 8 }}>
            <Text style={{ fontSize: 11, fontWeight: "bold", marginBottom: 3 }}>
                {title}
            </Text>

            <View style={styles.table}>
                <View style={[styles.row, styles.head]}>
                    <Text style={[styles.cell, { flex: 0.9 }]}>Type</Text>
                    <Text style={[styles.cell, { flex: 2.6 }]}>Bygningsdel</Text>
                    <Text style={[styles.cell, { flex: 1 }]}>U/Psi</Text>
                    <Text style={[styles.cell, { flex: 1 }]}>A/L</Text>
                </View>

                {list.map((row, index) => {
                    const isLine = row.partKind === KIND.LINE || row.partKind === "line";

                    return (
                        <View key={row.id || index} style={styles.row}>
                            <Text style={[styles.cell, { flex: 0.9 }]}>
                                {isLine ? "Linje" : "Flade"}
                            </Text>
                            <Text style={[styles.cell, { flex: 2.6 }]}>
                                {rowLabel(row)}
                            </Text>
                            <Text style={[styles.cell, { flex: 1 }]}>
                                {fmt(row.UorPsi, 2)}
                            </Text>
                            <Text style={[styles.cell, { flex: 1 }]}>
                                {fmt(row.AorL, 2)}
                            </Text>
                        </View>
                    );
                })}
            </View>
        </View>
    );
}

function VarmetabSummaryTable({ rows }) {
    return (
        <View style={styles.table}>
            <View style={[styles.row, styles.head]}>
                {rows.map((row) => (
                    <Text key={row.label} style={[styles.cell, { flex: 1 }]}>
                        {row.label}
                    </Text>
                ))}
            </View>

            <View style={styles.row}>
                {rows.map((row) => (
                    <Text key={row.label} style={[styles.cell, { flex: 1 }]}>
                        {row.value}
                    </Text>
                ))}
            </View>
        </View>
    );
}

function VarmetabSection({ data }) {
    const payload = normaliseVarmetabData(data);
    if (!payload) return null;

    const info = payload.info ?? {};
    const projectType = info.projectType ?? "tilbygning";
    const bType = info.bType ?? "helårs";
    const floorArea = info.floorArea ?? "";

    const existPlan = cleanVarmetabRows(payload.existPlan);
    const existLine = cleanVarmetabRows(payload.existLine);
    const coverPlan = cleanVarmetabRows(payload.coverPlan);
    const coverLine = cleanVarmetabRows(payload.coverLine);
    const designPlan = cleanVarmetabRows(payload.designPlan);
    const designLine = cleanVarmetabRows(payload.designLine);

    const hasRows =
        existPlan.length ||
        existLine.length ||
        coverPlan.length ||
        coverLine.length ||
        designPlan.length ||
        designLine.length;

    if (!hasRows) {
        return (
            <View>
                <Text style={styles.h2}>Varmetabsramme</Text>
                <Text>Ingen varmetabsdata fundet i projektet.</Text>
            </View>
        );
    }

    const design = sumHeatLoss([...designPlan, ...designLine]);
    const isTilbygning = projectType === "tilbygning";

    if (isTilbygning) {
        const {
            refPlan,
            refLine,
            glassLimit,
            glassArea,
            extraGlass,
        } = buildVarmetabReferenceRows({
            designPlan,
            designLine,
            coverPlan,
            bType,
            floorArea,
        });

        const reference = sumHeatLoss([...refPlan, ...refLine]);
        const diff = design - reference;
        const ok = design <= reference;

        return (
            <View>
                <Text style={styles.h2}>Varmetabsramme</Text>

                <Text style={styles.p}>
                    Projekttype: Tilbygning. Bygningstype: {bType === "sommer" ? "Sommerhus" : "Helårshus"}.
                    {floorArea ? ` Etageareal: ${fmt(floorArea, 1)} m².` : ""}
                </Text>

                <VarmetabSummaryTable
                    rows={[
                        { label: "Reference W", value: fmt(reference, 1) },
                        { label: "Design W", value: fmt(design, 1) },
                        { label: "Afvigelse W", value: `${diff >= 0 ? "+" : ""}${fmt(diff, 1)}` },
                        { label: "Status", value: ok ? "OK" : "Overskrider" },
                    ]}
                />

                {glassLimit != null && (
                    <Text style={{ marginTop: 4 }}>
                        Glasareal: {fmt(glassArea, 1)} m². Tilladt glasareal: {fmt(glassLimit, 1)} m².
                        {extraGlass > 0 ? ` Overskydende glas: ${fmt(extraGlass, 1)} m².` : ""}
                    </Text>
                )}

                <VarmetabRowsTable
                    title="Design"
                    rows={[...designPlan, ...designLine]}
                />

                <VarmetabRowsTable
                    title="Fradrag / dækkede konstruktioner"
                    rows={coverPlan}
                />

                <VarmetabRowsTable
                    title="Reference"
                    rows={[...refPlan, ...refLine]}
                />

                {info.note && (
                    <Text style={{ marginTop: 4 }}>{String(info.note)}</Text>
                )}
            </View>
        );
    }

    const exist = sumHeatLoss([...existPlan, ...existLine]);
    const cover = sumHeatLoss([...coverPlan, ...coverLine]);
    const netto = exist - 0.5 * cover;
    const diff = design - netto;
    const ok = design <= netto;

    return (
        <View>
            <Text style={styles.h2}>Varmetabsramme</Text>

            <Text style={styles.p}>
                Projekttype: Renovering. Bygningstype: {bType === "sommer" ? "Sommerhus" : "Helårshus"}.
                {floorArea ? ` Etageareal: ${fmt(floorArea, 1)} m².` : ""}
            </Text>

            <VarmetabSummaryTable
                rows={[
                    { label: "Netto-ramme W", value: fmt(netto, 1) },
                    { label: "Design W", value: fmt(design, 1) },
                    { label: "Afvigelse W", value: `${diff >= 0 ? "+" : ""}${fmt(diff, 1)}` },
                    { label: "Status", value: ok ? "OK" : "Overskrider" },
                ]}
            />

            <RenovationDesignRowsTable
                title="Renovering - dimensionerede konstruktioner og kuldebroer"
                rows={[...designPlan, ...designLine]}
            />

            <RenovationComplianceTable
                designPlan={designPlan}
                designLine={designLine}
            />

            {(existPlan.length || existLine.length) > 0 && (
                <VarmetabRowsTable
                    title="Eksisterende ramme"
                    rows={[...existPlan, ...existLine]}
                />
            )}

            {(coverPlan.length || coverLine.length) > 0 && (
                <VarmetabRowsTable
                    title="Dækkede konstruktioner"
                    rows={[...coverPlan, ...coverLine]}
                />
            )}

            {info.note && (
                <Text style={{ marginTop: 4 }}>{String(info.note)}</Text>
            )}
        </View>
    );
} function hasSavingsRows(data) {
    if (!data) return false;
    if (Array.isArray(data)) return data.length > 0;
    return typeof data === "object";
}

function SavingsSection({ data }) {
    if (!data) return null;

    const list = Array.isArray(data) ? data : [data];

    const colStyles = {
        scenario: { flex: 2.2 },
        kwh: { flex: 1.2, textAlign: "right" },
        kroner: { flex: 1.2, textAlign: "right" },
        fuel: { flex: 1.4, textAlign: "right" },
    };

    const fmt0 = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? fmt(n, 0) : "-";
    };

    const fuelText = (s) => {
        const amount = fmt0(s.annualUnits);
        const unit = s.unit || "enheder";
        return `${amount} ${unit}`;
    };

    return (
        <View>
            <Text style={styles.h2}>Årlig besparelse</Text>

            <View style={styles.table}>
                <View style={[styles.row, styles.head]}>
                    <Text style={[styles.cell, colStyles.scenario]}>Scenarie</Text>
                    <Text style={[styles.cell, colStyles.kwh]}>kWh/år</Text>
                    <Text style={[styles.cell, colStyles.kroner]}>kr/år</Text>
                    <Text style={[styles.cell, colStyles.fuel]}>Brændsel</Text>
                </View>

                {list.map((s, i) => (
                    <View key={i} style={styles.row}>
                        <Text style={[styles.cell, colStyles.scenario]}>
                            {s.name || "Scenarie"}
                        </Text>

                        <Text style={[styles.cell, colStyles.kwh]}>
                            {fmt0(s.kWhSaved)}
                        </Text>

                        <Text style={[styles.cell, colStyles.kroner]}>
                            {fmt0(s.annualCost)}
                        </Text>

                        <Text style={[styles.cell, colStyles.fuel]}>
                            {fuelText(s)}
                        </Text>
                    </View>
                ))}
            </View>

            {list.some((s) => s.fuelName || s.unit) && (
                <Text style={{ marginTop: 4 }}>
                    Brændsel: {list[0].fuelName || ""} ({list[0].unit || "enheder"}). Pris angives som kr pr. {list[0].unit || "enhed"}.
                </Text>
            )}
        </View>
    );
}
function SectionPage({
    projectName,
    author,
    reportNote = "",
    dateStr,
    logoUri,
    children,
}) {
    return (
        <Page size="A4" style={styles.page}>
            <HeaderBlock
                projectName={projectName}
                author={author}
                reportNote={reportNote}
                dateStr={dateStr}
                logoData={logoUri}
            />
            {children}
            <Footer projectName={projectName} />
        </Page>
    );
}

function ReportDocument({
    projectName,
    uvalueData,
    varmetabData,
    savingsData,
    author,
    reportNote,
    dateStr,
    logoUri,
}) {
    const hasUvalue = Array.isArray(uvalueData) && uvalueData.length > 0;
    const hasVarmetab = hasVarmetabRows(varmetabData);
    const hasSavings = hasSavingsRows(savingsData);

    const hasAnySection = hasUvalue || hasVarmetab || hasSavings;

    return (
        <Document>
            {hasUvalue && (
                <SectionPage
                    projectName={projectName}
                    author={author}
                    reportNote={reportNote}
                    dateStr={dateStr}
                    logoUri={logoUri}
                >
                    <UvalueSection deck={uvalueData} />
                </SectionPage>
            )}

            {hasVarmetab && (
                <SectionPage
                    projectName={projectName}
                    author={author}
                    reportNote={hasUvalue ? "" : reportNote}
                    dateStr={dateStr}
                    logoUri={logoUri}
                >
                    <VarmetabSection data={varmetabData} />
                </SectionPage>
            )}

            {hasSavings && (
                <SectionPage
                    projectName={projectName}
                    author={author}
                    reportNote={hasUvalue || hasVarmetab ? "" : reportNote}
                    dateStr={dateStr}
                    logoUri={logoUri}
                >
                    <SavingsSection data={savingsData} />
                </SectionPage>
            )}

            {!hasAnySection && (
                <SectionPage
                    projectName={projectName}
                    author={author}
                    reportNote={reportNote}
                    dateStr={dateStr}
                    logoUri={logoUri}
                >
                    <Text>Ingen data valgt til rapporten.</Text>
                </SectionPage>
            )}
        </Document>
    );
}

async function buildReportDocument({
    projectName = "",
    uvalueData = [],
    varmetabData = null,
    savingsData = null,
    author = "",
    reportNote = "",
    basePath = undefined,
    assetPrefix = undefined,
} = {}) {
    const urlOpts = { basePath, assetPrefix };
    await ensurePdfFont(urlOpts);

    const now = new Date();
    const dateStr = `${now.getDate()}. ${MONTHS_DA[now.getMonth()]} ${now.getFullYear()}`;

    const logoUri =
        (await asDataURI(withBase("images/system/isoverlogo.png", urlOpts))) ||
        (await asDataURI(withBase("images/isoverlogo.png", urlOpts))) ||
        null;

    return {
        now,
        doc: (
            <ReportDocument
                projectName={projectName}
                uvalueData={uvalueData}
                varmetabData={varmetabData}
                savingsData={savingsData}
                author={author}
                reportNote={reportNote}
                dateStr={dateStr}
                logoUri={logoUri}
            />
        ),
    };
}

export async function downloadPdfReport(options = {}) {
    const projectName = options.projectName || "";

    const { now, doc } = await buildReportDocument(options);

    const blob = await pdf(doc).toBlob();

    const fname = `${(projectName || "Energiberegner")
        .replace(/[\\/:*?"<>|]+/g, "_")} - ${now.toISOString().slice(0, 10)}.pdf`;

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = fname;

    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(url);
}

export async function buildPdfBlob(opts = {}) {
    const { doc } = await buildReportDocument(opts);
    return pdf(doc).toBlob();
}